import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { useAuth } from '@/shared/lib/auth';
import { t } from '@/i18n';

/** Axios instance for Netxol Core. Adds the bearer token and transparently refreshes it. */
export const core = axios.create({ baseURL: '/api/core', timeout: 30_000 });

let refreshing: Promise<string | null> | null = null;

/** A new access token from the HttpOnly refresh cookie (a support session has none and ends). */
async function refresh(): Promise<string | null> {
  const { setSession, clear } = useAuth.getState();
  try {
    const { data } = await axios.post('/api/core/auth/refresh', {});
    setSession(data);
    return data.accessToken as string;
  } catch {
    clear();
    return null;
  }
}

core.interceptors.request.use(async (cfg) => {
  let { accessToken, expiresAt } = useAuth.getState();
  // refresh ~1 minute before expiry
  if (accessToken && expiresAt && expiresAt * 1000 - Date.now() < 60_000) {
    refreshing ??= refresh().finally(() => { refreshing = null; });
    accessToken = await refreshing;
  }
  if (accessToken) cfg.headers.Authorization = `Bearer ${accessToken}`;
  cfg.headers['Accept-Language'] = localStorage.getItem('nx.lang') ?? 'en';   // server messages in the user's language
  return cfg;
});

core.interceptors.response.use(undefined, async (err: AxiosError) => {
  const cfg = err.config as AxiosRequestConfig & { _retried?: boolean };
  if (err.response?.status === 401 && cfg && !cfg._retried && !String(cfg.url).includes('/auth/')) {
    cfg._retried = true;
    refreshing ??= refresh().finally(() => { refreshing = null; });
    const tok = await refreshing;
    if (tok) {
      cfg.headers = { ...(cfg.headers ?? {}), Authorization: `Bearer ${tok}` };
      return core.request(cfg);
    }
  }
  return Promise.reject(err);
});

export function apiError(e: unknown): string {
  const ax = e as AxiosError<{ message?: string; details?: { fieldErrors?: Record<string, string[]>; summary?: string } }>;
  const d = ax?.response?.data;
  if (d?.details?.summary) return d.details.summary;   // Core: "<field label>: <reason>" in the user's language
  if (d?.details?.fieldErrors) {
    const first = Object.entries(d.details.fieldErrors)[0];
    if (first) return `${first[0]}: ${first[1][0]}`;
  }
  if (d?.message) return d.message;
  if (axios.isAxiosError(e)) return t(e.response ? 'ui.request_failed' : 'ui.network_error');
  return (e as Error)?.message || t('ui.request_failed');
}

export interface Paged<T> { items: T[]; page: number; pageSize: number; total: number; pages: number }
